import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, CircularProgress, Alert,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Plot from "react-plotly.js";
import * as XLSX from "xlsx";
import { MOCK_ZONAS, MOCK_TECNOLOGIAS } from "../../api/mockCalidadDatos";
import { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

const LINE_COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
];
const BAND_COLORS = [
  "rgba(31,119,180,0.15)",  "rgba(255,127,14,0.15)",  "rgba(44,160,44,0.15)",
  "rgba(214,39,40,0.15)",   "rgba(148,103,189,0.15)", "rgba(140,86,75,0.15)",
  "rgba(227,119,194,0.15)", "rgba(127,127,127,0.15)", "rgba(188,189,34,0.15)",
  "rgba(23,190,207,0.15)",
];

const DAY_OPTIONS = [1, 7, 30, 90, 180];

const METRICAS = [
  { key: "subida",          label: "Tráfico promedio de subida",           unit: "Mbps", description: "Distribución estadística del tráfico promedio de subida por operador en el período seleccionado.", convert: (v) => (v * 8) / 1e6 },
  { key: "bajada",          label: "Tráfico promedio de bajada",           unit: "Mbps", description: "Distribución estadística del tráfico promedio de bajada por operador en el período seleccionado.", convert: (v) => (v * 8) / 1e6 },
  { key: "interac_bajada",  label: "Tráfico bajada internacional",         unit: "Mbps", description: "Distribución estadística del tráfico de bajada internacional por operador.",                        convert: (v) => (v * 8) / 1e6 },
  { key: "latencia",        label: "Latencia",                             unit: "ms",   description: "Distribución estadística de la latencia promedio por operador en el período seleccionado.",         convert: (v) => v },
  { key: "perdida_paquetes",label: "Pérdida de paquetes",                  unit: "%",    description: "Distribución estadística de la pérdida de paquetes por operador en el período seleccionado.",       convert: (v) => v },
];

const VISUALIZACIONES = [
  { key: "boxplot_evol",  label: "Cajas y bigotes - evolución" },
  { key: "boxplot_comp",  label: "Cajas y bigotes - comparar" },
  { key: "linea_mediana", label: "Serie temporal - mediana + IQR" },
  { key: "linea_media",   label: "Serie temporal - media ± σ" },
];

// Adapts one API row (dimension=operador) to the internal format.
// The API provides p50_aprox and p95_aprox; p25/p75 are approximated.
function adaptApiRow(row) {
  const ap25 = (mn, p50) => (mn != null && p50 != null) ? (mn + p50) / 2 : null;
  const ap75 = (p50, p95) => (p50 != null && p95 != null) ? (p50 + p95) / 2 : null;
  return {
    fecha:       row.fecha_bucket,
    id_operador: row.id_operador,
    // bajada (Bps)
    bajada_dia_mean: row.bajada_dia_mean,
    bajada_dia_std:  null,
    bajada_dia_min:  row.bajada_dia_min,
    bajada_dia_p25:  ap25(row.bajada_dia_min,        row.bajada_dia_p50_aprox),
    bajada_dia_p50:  row.bajada_dia_p50_aprox,
    bajada_dia_p75:  ap75(row.bajada_dia_p50_aprox,  row.bajada_dia_p95_aprox),
    bajada_dia_max:  row.bajada_dia_max,
    bajada_ven_mean: null, bajada_ven_std: null, bajada_ven_min: null,
    bajada_ven_p25: null, bajada_ven_p50: null, bajada_ven_p75: null, bajada_ven_max: null,
    // subida (Bps)
    subida_dia_mean: row.subida_dia_mean,
    subida_dia_std:  null,
    subida_dia_min:  row.subida_dia_min,
    subida_dia_p25:  ap25(row.subida_dia_min,        row.subida_dia_p50_aprox),
    subida_dia_p50:  row.subida_dia_p50_aprox,
    subida_dia_p75:  ap75(row.subida_dia_p50_aprox,  row.subida_dia_p95_aprox),
    subida_dia_max:  row.subida_dia_max,
    subida_ven_mean: null, subida_ven_std: null, subida_ven_min: null,
    subida_ven_p25: null, subida_ven_p50: null, subida_ven_p75: null, subida_ven_max: null,
    // latencia (ms)
    latencia_dia_mean: row.latencia_dia_mean,
    latencia_dia_std:  null,
    latencia_dia_min:  row.latencia_dia_min,
    latencia_dia_p25:  ap25(row.latencia_dia_min,       row.latencia_dia_p50_aprox ?? row.latencia_dia_mean),
    latencia_dia_p50:  row.latencia_dia_p50_aprox ?? row.latencia_dia_mean ?? null,
    latencia_dia_p75:  ap75(row.latencia_dia_p50_aprox ?? row.latencia_dia_mean, row.latencia_dia_p95_aprox),
    latencia_dia_max:  row.latencia_dia_max,
    latencia_ven_mean: null, latencia_ven_std: null, latencia_ven_min: null,
    latencia_ven_p25: null, latencia_ven_p50: null, latencia_ven_p75: null, latencia_ven_max: null,
    // perdida_paquetes (raw)
    perdida_paquetes_dia_mean: row.perdida_paquetes_dia_mean,
    perdida_paquetes_dia_std:  null,
    perdida_paquetes_dia_min:  row.perdida_paquetes_dia_min,
    perdida_paquetes_dia_p25:  null,
    perdida_paquetes_dia_p50:  null,
    perdida_paquetes_dia_p75:  null,
    perdida_paquetes_dia_max:  row.perdida_paquetes_dia_max,
    perdida_paquetes_ven_mean: null, perdida_paquetes_ven_std: null, perdida_paquetes_ven_min: null,
    perdida_paquetes_ven_p25: null, perdida_paquetes_ven_p50: null, perdida_paquetes_ven_p75: null, perdida_paquetes_ven_max: null,
    // interac_bajada not provided by this API endpoint
    interac_bajada_dia_mean: null, interac_bajada_dia_std: null, interac_bajada_dia_min: null,
    interac_bajada_dia_p25: null, interac_bajada_dia_p50: null, interac_bajada_dia_p75: null, interac_bajada_dia_max: null,
    interac_bajada_ven_mean: null, interac_bajada_ven_p25: null, interac_bajada_ven_p50: null,
    interac_bajada_ven_p75: null, interac_bajada_ven_min: null, interac_bajada_ven_max: null,
  };
}

// ── trace builders ─────────────────────────────────────────────────────────

function buildBoxPlotTraces(rows, metricKey, ventana, convert, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byOpDate = {};
  rows.forEach((r) => {
    const k = `${r.id_operador}||${r.fecha}`;
    if (!byOpDate[k]) byOpDate[k] = { opId: r.id_operador, fecha: r.fecha, vals: [] };
    const p50 = r[P("p50")];
    if (p50 != null) byOpDate[k].vals.push({ p25: r[P("p25")], p50, p75: r[P("p75")], mn: r[P("min")], mx: r[P("max")] });
  });
  const opData = {};
  Object.values(byOpDate).forEach(({ opId, fecha, vals }) => {
    if (!vals.length) return;
    if (!opData[opId]) opData[opId] = [];
    const avg = (arr) => { const a = arr.filter((v) => v != null); return a.length ? a.reduce((x, y) => x + y, 0) / a.length : null; };
    const c = convert;
    const p25 = avg(vals.map((v) => v.p25));
    const p50 = avg(vals.map((v) => v.p50));
    const p75 = avg(vals.map((v) => v.p75));
    const mn  = vals.map((v) => v.mn).filter((v) => v != null && isFinite(v));
    const mx  = vals.map((v) => v.mx).filter((v) => v != null && isFinite(v));
    opData[opId].push({
      x: fecha,
      p25: p25 != null ? c(p25) : null,
      p50: c(p50),
      p75: p75 != null ? c(p75) : null,
      mn:  mn.length ? c(Math.min(...mn)) : null,
      mx:  mx.length ? c(Math.max(...mx)) : null,
    });
  });
  return Object.entries(opData).map(([opId, pts], i) => {
    const sorted = pts.sort((a, b) => a.x.localeCompare(b.x));
    const op = operators.find((o) => o.id_operador === Number(opId));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    return { type: "box", name: op?.nombre_operador ?? `Op ${opId}`, x: sorted.map((p) => p.x), q1: sorted.map((p) => p.p25), median: sorted.map((p) => p.p50), q3: sorted.map((p) => p.p75), lowerfence: sorted.map((p) => p.mn), upperfence: sorted.map((p) => p.mx), marker: { color }, boxmean: false };
  });
}

function buildBoxCompTraces(rows, metricKey, ventana, convert, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byOp = {};
  rows.forEach((r) => {
    const p50 = r[P("p50")];
    if (p50 == null) return;
    if (!byOp[r.id_operador]) byOp[r.id_operador] = { p25s: [], p50s: [], p75s: [], mins: [], maxs: [] };
    const d = byOp[r.id_operador];
    if (r[P("p25")] != null) d.p25s.push(r[P("p25")]);
    d.p50s.push(p50);
    if (r[P("p75")] != null) d.p75s.push(r[P("p75")]);
    const mn = r[P("min")]; if (mn != null && isFinite(mn)) d.mins.push(mn);
    const mx = r[P("max")]; if (mx != null && isFinite(mx)) d.maxs.push(mx);
  });
  const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  return Object.entries(byOp).map(([opId, d], i) => {
    const op = operators.find((o) => o.id_operador === Number(opId));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const c = convert;
    const p50agg = avg(d.p50s); if (p50agg == null) return null;
    const p25agg = d.p25s.length ? avg(d.p25s) : null;
    const p75agg = d.p75s.length ? avg(d.p75s) : null;
    const minagg = d.mins.length ? Math.min(...d.mins) : null;
    const maxagg = d.maxs.length ? Math.max(...d.maxs) : null;
    const name = op?.nombre_operador ?? `Op ${opId}`;
    return {
      type: "box", name,
      x: [name],
      q1:         p25agg != null ? [c(p25agg)] : [null],
      median:     [c(p50agg)],
      q3:         p75agg != null ? [c(p75agg)] : [null],
      lowerfence: minagg != null ? [c(minagg)] : [null],
      upperfence: maxagg != null ? [c(maxagg)] : [null],
      marker: { color },
    };
  }).filter(Boolean);
}

function buildLineMedianTraces(rows, metricKey, ventana, convert, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const byOpDate = {};
  rows.forEach((r) => {
    const k = `${r.id_operador}||${r.fecha}`;
    if (!byOpDate[k]) byOpDate[k] = { opId: r.id_operador, fecha: r.fecha, p25s: [], p50s: [], p75s: [] };
    if (r[P("p50")] != null) { byOpDate[k].p25s.push(r[P("p25")]); byOpDate[k].p50s.push(r[P("p50")]); byOpDate[k].p75s.push(r[P("p75")]); }
  });
  const opData = {};
  Object.values(byOpDate).forEach(({ opId, fecha, p25s, p50s, p75s }) => {
    if (!p50s.length) return;
    if (!opData[opId]) opData[opId] = { x: [], p25: [], p50: [], p75: [] };
    const avg = (arr) => { const a = arr.filter((v) => v != null); return a.length ? a.reduce((x, y) => x + y, 0) / a.length : null; };
    opData[opId].x.push(fecha);
    opData[opId].p25.push(avg(p25s) != null ? convert(avg(p25s)) : null);
    opData[opId].p50.push(convert(avg(p50s)));
    opData[opId].p75.push(avg(p75s) != null ? convert(avg(p75s)) : null);
  });
  const traces = [];
  Object.entries(opData).forEach(([opId, d], i) => {
    const sorted = d.x.map((x, j) => ({ x, p25: d.p25[j], p50: d.p50[j], p75: d.p75[j] })).sort((a, b) => a.x.localeCompare(b.x));
    const op = operators.find((o) => o.id_operador === Number(opId));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const bc    = BAND_COLORS[i % BAND_COLORS.length];
    const name = op?.nombre_operador ?? `Op ${opId}`;
    const hasIqr = sorted.some((s) => s.p25 != null && s.p75 != null);
    if (hasIqr) {
      traces.push({ type: "scatter", x: [...sorted.map((s) => s.x), ...sorted.map((s) => s.x).reverse()], y: [...sorted.map((s) => s.p75), ...sorted.map((s) => s.p25).reverse()], fill: "toself", fillcolor: bc, line: { color: "rgba(0,0,0,0)" }, showlegend: false, hoverinfo: "skip" });
    }
    traces.push({ type: "scatter", mode: "lines+markers", name, x: sorted.map((s) => s.x), y: sorted.map((s) => s.p50), line: { color, width: 2 }, marker: { size: 4, color } });
  });
  return traces;
}

function buildLineMeanTraces(rows, metricKey, ventana, convert, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const meanCol = `${prefix}_mean`;
  const stdCol  = `${prefix}_std`;
  const byOpDate = {};
  rows.forEach((r) => {
    const k = `${r.id_operador}||${r.fecha}`;
    if (!byOpDate[k]) byOpDate[k] = { opId: r.id_operador, fecha: r.fecha, means: [], stds: [] };
    if (r[meanCol] != null) { byOpDate[k].means.push(r[meanCol]); byOpDate[k].stds.push(r[stdCol] ?? 0); }
  });
  const opData = {};
  Object.values(byOpDate).forEach(({ opId, fecha, means, stds }) => {
    if (!means.length) return;
    if (!opData[opId]) opData[opId] = { x: [], mean: [], std: [] };
    const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
    opData[opId].x.push(fecha); opData[opId].mean.push(convert(avg(means))); opData[opId].std.push(convert(avg(stds)));
  });
  const traces = [];
  Object.entries(opData).forEach(([opId, d], i) => {
    const sorted = d.x.map((x, j) => ({ x, mean: d.mean[j], std: d.std[j] })).sort((a, b) => a.x.localeCompare(b.x));
    const op = operators.find((o) => o.id_operador === Number(opId));
    const color = LINE_COLORS[i % LINE_COLORS.length];
    const bc    = BAND_COLORS[i % BAND_COLORS.length];
    const name = op?.nombre_operador ?? `Op ${opId}`;
    const hi = sorted.map((s) => s.mean + s.std);
    const lo = sorted.map((s) => Math.max(0, s.mean - s.std));
    if (sorted.some((s) => s.std > 0)) {
      traces.push({ type: "scatter", x: [...sorted.map((s) => s.x), ...sorted.map((s) => s.x).reverse()], y: [...hi, ...lo.reverse()], fill: "toself", fillcolor: bc, line: { color: "rgba(0,0,0,0)" }, showlegend: false, hoverinfo: "skip" });
    }
    traces.push({ type: "scatter", mode: "lines+markers", name, x: sorted.map((s) => s.x), y: sorted.map((s) => s.mean), line: { color, width: 2 }, marker: { size: 4, color } });
  });
  return traces;
}

function exportXlsx(rows, metricKey, ventana, convert, unit, operators) {
  const prefix = `${metricKey}_${ventana}`;
  const P = (col) => `${prefix}_${col}`;
  const data = rows.filter((r) => r[P("p50")] != null || r[P("mean")] != null).map((r) => {
    const op = operators.find((o) => o.id_operador === r.id_operador);
    return {
      fecha: r.fecha, operador: op?.nombre_operador ?? `Op ${r.id_operador}`,
      [`min (${unit})`]:  r[P("min")]  != null ? Number(convert(r[P("min")]).toFixed(4))  : null,
      [`p25 (${unit})`]:  r[P("p25")]  != null ? Number(convert(r[P("p25")]).toFixed(4))  : null,
      [`p50 (${unit})`]:  r[P("p50")]  != null ? Number(convert(r[P("p50")]).toFixed(4))  : null,
      [`p75 (${unit})`]:  r[P("p75")]  != null ? Number(convert(r[P("p75")]).toFixed(4))  : null,
      [`max (${unit})`]:  r[P("max")]  != null ? Number(convert(r[P("max")]).toFixed(4))  : null,
      [`mean (${unit})`]: r[P("mean")] != null ? Number(convert(r[P("mean")]).toFixed(4)) : null,
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, metricKey);
  XLSX.writeFile(wb, `calidad_${metricKey}_${ventana}.xlsx`);
}

const downloadBtnSx = {
  border: "2px solid #003B8D", borderRadius: "4px", color: "#003B8D",
  backgroundColor: "#FFF", textTransform: "none", fontSize: "13px",
  padding: "6px 16px", fontWeight: 500, "&:hover": { backgroundColor: "#f0f4ff" },
};

export default function CalidadDatos() {
  const [operators,     setOperators]     = useState([]);
  const [apiData,       setApiData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  const [selectedDays,  setSelectedDays]  = useState(7);
  const [selectedOps,   setSelectedOps]   = useState([]);
  const [selectedZona,  setSelectedZona]  = useState("all");
  const [selectedRbd,   setSelectedRbd]   = useState("all");
  const [selectedTec,   setSelectedTec]   = useState("all");
  const [ventana,       setVentana]       = useState("dia");
  const [metricaKey,    setMetricaKey]    = useState("subida");
  const [visualizacion, setVisualizacion] = useState("boxplot_evol");

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        setSelectedOps(ops.map((o) => o.id_operador));
      })
      .catch(() => {});
  }, []);

  // Fetch stats whenever date range or server-side filters change
  useEffect(() => {
    const today = new Date();
    const fechaFin   = today.toISOString().split("T")[0];
    const start = new Date(today);
    start.setDate(start.getDate() - selectedDays + 1);
    const fechaInicio = start.toISOString().split("T")[0];

    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: "/service/reportes/stats-rbd/agregado",
      params: {
        granularidad: "1d",
        dimension: "operador",
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        ...(selectedZona !== "all" ? { id_zona: Number(selectedZona) }   : {}),
        ...(selectedRbd  !== "all" ? { rbd: selectedRbd }                : {}),
        ...(selectedTec  !== "all" ? { tipo_tecnologia: Number(selectedTec) } : {}),
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map(adaptApiRow)))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [selectedDays, selectedZona, selectedRbd, selectedTec]);

  const selectedMetrica = METRICAS.find((m) => m.key === metricaKey);

  // RBDs derived from current data (empty for dimension=operador, kept for UI compatibility)
  const allRbds = useMemo(() => [...new Set(apiData.map((r) => r.rbd).filter(Boolean))].sort(), [apiData]);

  // Client-side operator filter only (date/zona/rbd/tec are server-side)
  const filteredRows = useMemo(() => {
    if (!selectedOps.length) return apiData;
    return apiData.filter((r) => selectedOps.includes(r.id_operador));
  }, [apiData, selectedOps]);

  const traces = useMemo(() => {
    if (!selectedMetrica) return [];
    const { key, convert } = selectedMetrica;
    switch (visualizacion) {
      case "boxplot_evol":  return buildBoxPlotTraces(filteredRows, key, ventana, convert, operators);
      case "boxplot_comp":  return buildBoxCompTraces(filteredRows, key, ventana, convert, operators);
      case "linea_mediana": return buildLineMedianTraces(filteredRows, key, ventana, convert, operators);
      case "linea_media":   return buildLineMeanTraces(filteredRows, key, ventana, convert, operators);
      default:              return buildBoxPlotTraces(filteredRows, key, ventana, convert, operators);
    }
  }, [filteredRows, metricaKey, ventana, visualizacion, selectedMetrica, operators]);

  const isBoxplot = visualizacion.startsWith("boxplot");

  const plotLayout = useMemo(() => ({
    margin: { l: 60, r: 50, t: 50, b: 110 },
    hovermode: "x",
    legend: {
      orientation: "h", yanchor: "top", y: -0.18, xanchor: "center", x: 0.5,
      bgcolor: "rgba(255,255,255,0.7)", bordercolor: "#ddd", borderwidth: 1,
      font: { size: 10 },
    },
    xaxis: { tickfont: { size: 10 }, automargin: true },
    yaxis: { tickfont: { size: 10 }, title: selectedMetrica?.unit ?? "", automargin: true },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "#fafafa",
    boxmode: isBoxplot ? "overlay" : undefined,
    height: isBoxplot ? 500 : 460,
  }), [isBoxplot, visualizacion, selectedMetrica]);

  const handleClearFilters = useCallback(() => {
    setSelectedDays(7);
    setSelectedOps(operators.map((o) => o.id_operador));
    setSelectedZona("all");
    setSelectedRbd("all");
    setSelectedTec("all");
    setVentana("dia");
  }, [operators]);

  const operadorOpts = useMemo(() => [
    { value: "all", label: "Todos los operadores" },
    ...operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
  ], [operators]);

  const opFilterValue = !selectedOps.length || selectedOps.length === operators.length
    ? "all"
    : String(selectedOps[0] ?? "all");

  return (
    <PageBody>
      {/* Título y descripción */}
      <Typography variant="h5" fontWeight="bold" textAlign="center"
        sx={{ letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Calidad de Datos
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3} maxWidth={700} mx="auto">
        Análisis estadístico de calidad de servicio por operador y período. Seleccione la métrica y
        tipo de visualización para explorar la distribución de los datos.
      </Typography>

      <Box sx={{ mx: "-3.5rem", display: "flex", gap: 2 }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: 260, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <Paper sx={{ p: 1.5, overflowX: "hidden" }}>

            <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>Filtros</Typography>

            {/* Day buttons */}
            <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", width: "fit-content" }}>
              {DAY_OPTIONS.map((d) => (
                <Button key={d} size="small" disableRipple
                  variant={selectedDays === d ? "contained" : "text"}
                  color="primary"
                  onClick={() => setSelectedDays(d)}
                  sx={{ minWidth: 44, px: 0.8, py: 0.4, fontSize: "0.72rem", textTransform: "none", borderRadius: 0, borderRight: d !== 180 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none" }}
                >
                  {d === 1 ? "1 día" : `${d} días`}
                </Button>
              ))}
            </Box>

            {/* Select all */}
            <Button fullWidth size="small" disableRipple
              onClick={() => setSelectedOps(operators.map((o) => o.id_operador))}
              sx={{ mb: 1.5, textTransform: "none", fontSize: "0.78rem", border: "1px solid #1976D2", color: "#1976D2", borderRadius: "4px" }}
            >
              Seleccionar todo el parque
            </Button>

            {/* Operador */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="operador" name="Operador"
                opt={operadorOpts} value={opFilterValue}
                callback={(_, val) => {
                  if (!val || val === "all") setSelectedOps(operators.map((o) => o.id_operador));
                  else setSelectedOps([Number(val)]);
                }}
              />
            </Box>

            {/* Zona */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="zona" name="Zona"
                opt={[{ value: "all", label: "Todas las zonas" }, ...MOCK_ZONAS.map((z) => ({ value: String(z.id), label: z.label }))]}
                value={selectedZona} callback={(_, val) => setSelectedZona(val ?? "all")}
              />
            </Box>

            {/* RBD */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="rbd" name="RBD"
                opt={[{ value: "all", label: "Todos los RBD" }, ...allRbds.map((r) => ({ value: String(r), label: String(r) }))]}
                value={selectedRbd} callback={(_, val) => setSelectedRbd(val ?? "all")} noSort
              />
            </Box>

            {/* Tecnología */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="tecnologia" name="Tecnología"
                opt={[{ value: "all", label: "Todas" }, ...MOCK_TECNOLOGIAS.map((t) => ({ value: String(t.id), label: t.label }))]}
                value={selectedTec} callback={(_, val) => setSelectedTec(val ?? "all")}
              />
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Métrica</Typography>
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="metrica" name="Métrica"
                opt={METRICAS.map((m) => ({ value: m.key, label: m.label }))}
                value={metricaKey} callback={(_, val) => val && setMetricaKey(val)} noSort
              />
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Visualización</Typography>
            <Box sx={{ mb: 1.5 }}>
              <InputFilter item="visualizacion" name="Visualización"
                opt={VISUALIZACIONES.map((v) => ({ value: v.key, label: v.label }))}
                value={visualizacion} callback={(_, val) => val && setVisualizacion(val)} noSort
              />
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Ventana</Typography>
            <RadioGroup value={ventana} onChange={(e) => setVentana(e.target.value)} sx={{ mb: 1.5 }}>
              <FormControlLabel value="ven" control={<Radio size="small" />} label={<Typography variant="body2">Ventana Horaria</Typography>} />
              <FormControlLabel value="dia" control={<Radio size="small" />} label={<Typography variant="body2">Día completo</Typography>} />
            </RadioGroup>

            {/* Chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
              {operators.filter((o) => selectedOps.includes(o.id_operador)).map((o) => (
                <Chip key={o.id_operador} label={o.nombre_operador} size="small"
                  onDelete={() => setSelectedOps((prev) => prev.filter((x) => x !== o.id_operador))}
                  sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                />
              ))}
            </Box>

            <Button variant="outlined" color="error" size="small" fullWidth disableRipple
              startIcon={<RemoveCircleOutlineIcon />} onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Limpiar todos los filtros
            </Button>
          </Paper>
        </Box>

        {/* ── Chart ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: 3.5 }}>
            {/* Fila: título (izq) + descripción (der) */}
            <Grid2 container spacing={2} alignItems="flex-start" mb={3}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ textTransform: "uppercase", lineHeight: 1.3 }}>
                  {selectedMetrica?.label}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 8 }}>
                <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                  {selectedMetrica?.description}
                </Typography>
              </Grid2>
            </Grid2>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 460 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {!loading && !error && (
              <Plot
                data={traces}
                layout={plotLayout}
                config={{ responsive: true, displayModeBar: true, displaylogo: false, modeBarButtonsToRemove: ["toImage"] }}
                style={{ width: "100%" }}
              />
            )}

            <Divider sx={{ mt: 1.5, mb: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => exportXlsx(filteredRows, metricaKey, ventana, selectedMetrica.convert, selectedMetrica.unit, operators)} sx={downloadBtnSx}>
                Descarga tabla (.csv)
              </Button>
            </Box>
          </Paper>
        </Box>

      </Box>
    </PageBody>
  );
}
